import React from 'react';
import { Animated, TouchableOpacity, View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useLiveSession } from '../../contexts/LiveSessionContext';

/**
 * GlobalLivePip
 *
 * Renders a draggable "Picture-in-Picture" bubble that floats above ALL
 * navigation screens whenever a live room is minimized.  It reads from
 * the global LiveSessionContext so it is available everywhere in the app.
 *
 * Mount this once, right inside <NavigationContainer> in App.js.
 */
export default function GlobalLivePip() {
    const { session, clearSession, pipPan, pipPanResponder } = useLiveSession();

    if (!session) return null;

    const {
        topic,
        mentorName,
        participantCount = 1,
        isMicEnabled = true,
        onToggleMic,
        onRestore,
        onLeave,
    } = session;

    return (
        <Animated.View
            style={[styles.pip, { transform: pipPan.getTranslateTransform() }]}
            {...pipPanResponder.panHandlers}
        >
            <LinearGradient colors={['#3b0764', '#1a003a']} style={styles.inner}>
                {/* Live chip + topic */}
                <View style={styles.topRow}>
                    <View style={styles.liveChip}>
                        <View style={styles.liveDot} />
                        <Text style={styles.liveText}>LIVE</Text>
                    </View>
                    <Text style={styles.topic} numberOfLines={1}>{topic}</Text>
                </View>

                <Text style={styles.mentor} numberOfLines={1}>by {mentorName}</Text>

                {/* Quick controls */}
                <View style={styles.controls}>
                    {/* Mic toggle */}
                    <TouchableOpacity
                        style={[styles.btn, !isMicEnabled && styles.btnOff]}
                        onPress={onToggleMic}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                        <Ionicons
                            name={isMicEnabled ? 'mic' : 'mic-off'}
                            size={14}
                            color="#fff"
                        />
                    </TouchableOpacity>

                    {/* Participant count */}
                    <View style={styles.peopleChip}>
                        <Ionicons name="people" size={11} color="#a78bfa" />
                        <Text style={styles.peopleText}>{participantCount}</Text>
                    </View>

                    {/* Restore */}
                    <TouchableOpacity style={styles.restoreBtn} onPress={onRestore}>
                        <Ionicons name="expand" size={12} color="#7c3aed" />
                        <Text style={styles.restoreText}>Restore</Text>
                    </TouchableOpacity>

                    {/* End session */}
                    <TouchableOpacity
                        style={styles.leaveBtn}
                        onPress={() => { clearSession(); onLeave?.(); }}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                        <Ionicons name="close" size={12} color="#f87171" />
                    </TouchableOpacity>
                </View>
            </LinearGradient>
        </Animated.View>
    );
}

const styles = StyleSheet.create({
    pip: {
        position: 'absolute',
        width: 185,
        borderRadius: 18,
        overflow: 'hidden',
        zIndex: 99999,
        elevation: 25,
        shadowColor: '#000',
        shadowOpacity: 0.5,
        shadowRadius: 20,
    },
    inner: {
        padding: 12,
        gap: 5,
    },
    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    liveChip: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(239,68,68,0.22)',
        paddingHorizontal: 7,
        paddingVertical: 2,
        borderRadius: 20,
        gap: 4,
    },
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: '#ef4444',
    },
    liveText: {
        color: '#ef4444',
        fontSize: 8,
        fontWeight: '900',
        letterSpacing: 0.5,
    },
    topic: {
        color: '#e2e8f0',
        fontSize: 11,
        fontWeight: '700',
        flex: 1,
    },
    mentor: {
        color: 'rgba(196,181,253,0.65)',
        fontSize: 10,
        fontWeight: '600',
        marginLeft: 2,
    },
    controls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 4,
    },
    btn: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: 'rgba(255,255,255,0.12)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    btnOff: {
        backgroundColor: 'rgba(239,68,68,0.35)',
    },
    peopleChip: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(124,58,237,0.35)',
        paddingHorizontal: 7,
        paddingVertical: 4,
        borderRadius: 10,
        gap: 3,
    },
    peopleText: {
        color: '#a78bfa',
        fontSize: 10,
        fontWeight: '800',
    },
    restoreBtn: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.18)',
        paddingVertical: 6,
        borderRadius: 10,
        gap: 4,
    },
    restoreText: {
        color: '#fff',
        fontSize: 10,
        fontWeight: '700',
    },
    leaveBtn: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: 'rgba(239,68,68,0.2)',
        justifyContent: 'center',
        alignItems: 'center',
    },
});
